import type { SwipeDirection } from "pageSwipe";
import * as pageTransitions from "pageTransitions";
import * as preact from "preact";
import { useState } from "preact/hooks";
import * as languageMap from "shared/languageMap";
import type { BookData } from "stores/BookStore";
import type ReaderTextStore from "stores/ReaderTextStore";
import type ReaderViewStore from "stores/ReaderViewStore";
import stores from "stores/stores";
import SyncAudioControls, {
  type SyncAudioControlsProps,
} from "SyncAudioControls";
import type { ApplyTranscriptMode } from "views/ApplyTranscriptDialog";
import ContextTranslationStrip, {
  type ContextTranslationUsage,
} from "views/ContextTranslationStrip";
import type { FormattingOptionsProps } from "views/FormattingOptions";
import ReaderFooterView, {
  type ReaderFooterState,
} from "views/ReaderFooterView";
import ReaderPageDropdownMenuView from "views/ReaderPageDropdownMenuView";
import type {
  DictionaryPaneState,
  SmartDictionaryPaneState,
} from "views/ReaderSidebarPanes";
import ReaderSidebarView, {
  ReaderSidebarButton,
} from "views/ReaderSidebarView";
import ReaderTextIsland from "views/ReaderTextIsland";
import ReaderTitleView from "views/ReaderTitleView";
import ReaderWalkthroughWidget from "views/ReaderWalkthroughWidget";
import StreakView from "views/StreakView";

type ReaderPageLayoutData = {
  book: BookData;
  owner?: boolean;
  contentHTML?: string;
  endOfBook?: boolean;
  hasAudioFile?: boolean;
  localMode?: boolean;
  scrollMode?: boolean;
  signedOutTopBarLanguageString?: string;
  showSignedOutTopBar?: boolean;
  showStreak?: boolean;
  streakHideUponCompletion?: boolean;
  streakReminderPrompt?: boolean;
  allowYouTube?: boolean;
  contextTranslationUsage?: ContextTranslationUsage;
  selectedTabName?: string;
};

const SignedOutTopBar = ({
  book,
  languageString,
}: {
  book: BookData;
  languageString?: string;
}) => {
  const [visible, setVisible] = useState(true);
  if (!visible || !languageString) {
    return <div class="signedOutTopBar"></div>;
  }

  return (
    <div class="signedOutTopBar" style={{ display: "flex" }}>
      <a href="/landing-page" data-bypass target="_blank">
        <img
          alt="logo"
          class="pointer logo"
          height="41"
          src="/content/rabbitLogo.svg"
          width="48"
        />
      </a>
      <div>
        Learn {languageString} with{" "}
        {book.category === "Fiction" ? "stories" : "texts"} like {book.title} on
        Readlang. Click to translate words and phrases and practice later with
        flashcards.{" "}
        <a href="#" class="signin">
          Sign in
        </a>{" "}
        or{" "}
        <a href="/landing-page" data-bypass target="_blank">
          Learn more
        </a>
        .
      </div>
      <div>
        <i
          class="icon icon-remove signedOutCloseButton"
          onClick={() => setVisible(false)}
        ></i>
      </div>
    </div>
  );
};

class SyncAudioMediaPlayerView extends preact.Component<{
  onElementChange?: (element: HTMLElement | undefined) => void;
}> {
  private playerRef = preact.createRef<HTMLDivElement>();

  componentDidMount() {
    this.props.onElementChange?.(this.playerRef.current ?? undefined);
  }

  componentWillUnmount() {
    this.props.onElementChange?.(undefined);
  }

  shouldComponentUpdate() {
    return false;
  }

  render() {
    return <div id="mediaPlayer" ref={this.playerRef}></div>;
  }
}

type Props = {
  appRouter?: any;
  /** How far the text is moved up, to stay in view above the bottom sheet */
  bookShift?: number;
  /** On phones, the sidebar is a sheet over the bottom of the page */
  bottomSheet?: boolean;
  bottomSheetHeight?: number;
  dictionaryPaneState?: DictionaryPaneState;
  footerState?: ReaderFooterState;
  formattingOptionsProps?: FormattingOptionsProps;
  mediaPanelHeight?: number;
  mediaPanelResizing?: boolean;
  onDeleteAudio?: () => void;
  onDismissSidebarHint?: () => void;
  onEditDocumentRefresh?: (options?: { reInitialize?: boolean }) => void;
  onMediaPanelHeightChange?: (height: number) => void;
  onMediaPanelResizingChange?: (resizing: boolean) => void;
  onMediaPlayerElementChange?: (element: HTMLElement | undefined) => void;
  onHomeClick?: preact.JSX.MouseEventHandler<HTMLAnchorElement>;
  onMarkCompleted?: () => void;
  onOpenDictionary?: () => void;
  onStreakReminderPromptClose?: () => void;
  onNextPage?: () => void;
  onPageNavigationFurthest?: () => void;
  onPageNavigationSpecific?: () => void;
  onPageNavigationStart?: () => void;
  onPageNumberClick?: () => void;
  onPreviousPage?: () => void;
  onSwipe?: (direction: SwipeDirection) => void;
  onReadAloudSkipBackward?: () => void;
  onReadAloudSkipForward?: () => void;
  onReadAloudToggle?: () => void;
  onRemoveYouTube?: () => void;
  onSaveYouTubeID?: (userInput: string) => void;
  onSectionChange?: (start: number) => void;
  onSourceLanguageChange?: (languageCode: string) => void;
  onSidebarTabSelect?: (tabName: string) => void;
  onSidebarToggle?: () => void;
  onStartReviewSession?: (toPosition: number) => void;
  /**
   * Same as selecting a tab + opening the sidebar if it's not already open.
   * Used by the dropdown menu's "Open Settings…" entry, which needs to make
   * the Settings tab visible regardless of current sidebar state.
   */
  onOpenSettings?: () => void;
  onSubmitYouTubeTranscript?: (
    transcript: string,
    mode: ApplyTranscriptMode
  ) => void | Promise<void>;
  pageData: ReaderPageLayoutData;
  pageTextViewportNoFooter?: boolean;
  readerTextStore?: ReaderTextStore;
  readerViewStore?: ReaderViewStore;
  scrollViewCompleted?: boolean;
  selectedTabName?: string;
  sidebarHintText?: string;
  sidebarHintTitle?: string;
  sidebarHintVisible?: boolean;
  sideBarVisible?: boolean;
  sideBarWidth: number;
  smartDictionaryPaneState?: SmartDictionaryPaneState;
  syncAudioControlsState?: SyncAudioControlsProps;
  titleBarVisible?: boolean;
};

const ReaderPageLayoutView = ({
  appRouter,
  bookShift,
  bottomSheet,
  bottomSheetHeight,
  dictionaryPaneState,
  footerState,
  formattingOptionsProps,
  mediaPanelHeight,
  mediaPanelResizing,
  onDeleteAudio,
  onDismissSidebarHint,
  onEditDocumentRefresh,
  onMediaPanelHeightChange,
  onMediaPanelResizingChange,
  onMediaPlayerElementChange,
  onHomeClick,
  onMarkCompleted,
  onOpenDictionary,
  onStreakReminderPromptClose,
  onNextPage,
  onPageNavigationFurthest,
  onPageNavigationSpecific,
  onPageNavigationStart,
  onPageNumberClick,
  onPreviousPage,
  onSwipe,
  onReadAloudSkipBackward,
  onReadAloudSkipForward,
  onReadAloudToggle,
  onRemoveYouTube,
  onSaveYouTubeID,
  onSectionChange,
  onSourceLanguageChange,
  onOpenSettings,
  onSidebarTabSelect,
  onSidebarToggle,
  onStartReviewSession,
  onSubmitYouTubeTranscript,
  pageData,
  pageTextViewportNoFooter,
  readerTextStore,
  readerViewStore,
  scrollViewCompleted,
  selectedTabName,
  sidebarHintText,
  sidebarHintTitle,
  sidebarHintVisible,
  sideBarVisible,
  sideBarWidth,
  smartDictionaryPaneState,
  syncAudioControlsState,
  titleBarVisible = true,
}: Props) => {
  const { book } = pageData;

  return (
    <>
      {pageData.showSignedOutTopBar ? (
        <SignedOutTopBar
          book={book}
          languageString={pageData.signedOutTopBarLanguageString}
        />
      ) : (
        <div class="signedOutTopBar"></div>
      )}
      <div class="outerContainer">
        <div
          class="container"
          style={{
            right: sideBarVisible && !bottomSheet ? `${sideBarWidth}px` : "0",
            transition: "right 200ms",
          }}
        >
          <div
            id="titleBar"
            class="titleBar"
            // The reminder prompt points at the streak indicator, so it lifts
            // every gate on the indicator's visibility while open
            style={{
              display:
                titleBarVisible || pageData.streakReminderPrompt
                  ? undefined
                  : "none",
            }}
          >
            <div class="homeButton">
              <a
                id="home"
                href="/previousLibraryOrDashboard"
                onClick={onHomeClick}
              >
                <i class="icon-chevron-left"></i>{" "}
                {pageTransitions.backTitle(
                  // Where it goes without a page to go back to (app.ts's
                  // previousLibraryOrDashboard)
                  localStorage.getItem("libraryOrDashboardPath") ?? "/dashboard"
                )}
              </a>
              <div
                class="flashcards tooltip arrowLeft"
                style={{ padding: "8px 12px", top: "-10px", left: "105%" }}
              >
                <h3>Start practising!</h3>
                <p>
                  We generated <span class="tooltip-readyToStartCount"></span>{" "}
                  flashcards for you while you were reading.
                </p>
              </div>
            </div>

            <div id="title">
              {readerViewStore && onSectionChange && (
                <ReaderTitleView
                  readerViewStore={readerViewStore}
                  onSectionChange={onSectionChange}
                />
              )}
            </div>
            {(pageData.showStreak || pageData.streakReminderPrompt) && (
              <div id="streak" class="titleBarWidget">
                <StreakView
                  store={stores.streak}
                  hideUponCompletion={
                    pageData.streakHideUponCompletion &&
                    !pageData.streakReminderPrompt
                  }
                  reminderPrompt={pageData.streakReminderPrompt}
                  onReminderPromptClose={onStreakReminderPromptClose}
                />
              </div>
            )}
            <div id="walkthroughWidget">
              <ReaderWalkthroughWidget
                readAloudEnabled={Boolean(
                  footerState?.readAloudAvailable &&
                    footerState?.readAloudVisible
                )}
              />
            </div>

            <div id="dropdownMenu" class="titleBarWidget">
              {appRouter && readerViewStore && (
                <ReaderPageDropdownMenuView
                  allowYouTube={pageData.allowYouTube}
                  appRouter={appRouter}
                  hasAudioFile={pageData.hasAudioFile}
                  isOwner={pageData.owner}
                  onDeleteAudio={onDeleteAudio}
                  onEditDocumentRefresh={onEditDocumentRefresh}
                  onRemoveYouTube={onRemoveYouTube}
                  onSaveYouTubeID={onSaveYouTubeID}
                  onSubmitYouTubeTranscript={onSubmitYouTubeTranscript}
                  readerViewStore={readerViewStore}
                  openSettings={onOpenSettings}
                  // On phones it would open behind the bottom sheet
                  onOpen={
                    bottomSheet && sideBarVisible ? onSidebarToggle : undefined
                  }
                />
              )}
            </div>

            <ReaderSidebarButton
              bottomSheet={bottomSheet}
              glow={sidebarHintVisible}
              hintText={sidebarHintText}
              hintTitle={sidebarHintTitle}
              onClick={onSidebarToggle}
              onDismissHint={onDismissSidebarHint}
              showHint={sidebarHintVisible}
              sideBarVisible={sideBarVisible}
            />
            <div class="titleBarWidget"></div>
          </div>

          <div
            id="mediaPanel"
            style={{
              height:
                mediaPanelHeight !== undefined
                  ? `${mediaPanelHeight}px`
                  : undefined,
            }}
          >
            <SyncAudioMediaPlayerView
              onElementChange={onMediaPlayerElementChange}
            />
            {syncAudioControlsState && (
              <SyncAudioControls
                {...syncAudioControlsState}
                mediaPanelHeight={mediaPanelHeight ?? 0}
                onMediaPanelHeightChange={(height) =>
                  onMediaPanelHeightChange?.(height)
                }
                setResizing={(resizing) =>
                  onMediaPanelResizingChange?.(resizing)
                }
              />
            )}
          </div>

          <div
            class="bookColumn"
            style={{
              transform: bookShift ? `translateY(${-bookShift}px)` : undefined,
              transition: "transform 200ms",
            }}
          >
            <div
              id="bookContent"
              style={{
                top:
                  mediaPanelHeight !== undefined
                    ? `${mediaPanelHeight + 40}px`
                    : undefined,
              }}
            >
              <ReaderTextIsland
                bookID={book._id}
                endOfText={pageData.endOfBook}
                noFooter={pageTextViewportNoFooter}
                onMarkCompleted={onMarkCompleted}
                onStartReviewSession={onStartReviewSession}
                onSwipe={onSwipe}
                readerTextStore={readerTextStore}
                readerViewStore={readerViewStore}
                scrollMode={Boolean(pageData.scrollMode)}
                scrollFooter={
                  pageData.scrollMode && (!pageData.localMode || book.public)
                    ? scrollViewCompleted
                      ? "endOfText"
                      : "completedButton"
                    : undefined
                }
                textDirection={
                  book.language &&
                  languageMap.map[book.language]?.direction === "RTL"
                    ? "rtl"
                    : undefined
                }
              />

              <ReaderFooterView
                footerState={footerState}
                onNextPage={onNextPage}
                onPageNavigationFurthest={onPageNavigationFurthest}
                onPageNavigationSpecific={onPageNavigationSpecific}
                onPageNavigationStart={onPageNavigationStart}
                onPageNumberClick={onPageNumberClick}
                onPreviousPage={onPreviousPage}
                onReadAloudSkipBackward={onReadAloudSkipBackward}
                onReadAloudSkipForward={onReadAloudSkipForward}
                onReadAloudToggle={onReadAloudToggle}
              />
            </div>
          </div>

          <div id="contextTranslationStripContainer">
            {pageData.contextTranslationUsage && book.language && (
              <ContextTranslationStrip
                initialUsage={pageData.contextTranslationUsage}
                language={book.language}
              />
            )}
          </div>
        </div>

        <ReaderSidebarView
          appRouter={appRouter}
          dictionaryPaneState={dictionaryPaneState}
          formattingOptionsProps={formattingOptionsProps}
          onOpenDictionary={onOpenDictionary}
          onSourceLanguageChange={onSourceLanguageChange}
          onSidebarToggle={onSidebarToggle}
          onTabSelect={onSidebarTabSelect}
          pageData={pageData}
          selectedTabName={selectedTabName}
          sideBarVisible={sideBarVisible}
          sideBarWidth={sideBarWidth}
          bottomSheet={bottomSheet}
          bottomSheetHeight={bottomSheetHeight}
          smartDictionaryPaneState={smartDictionaryPaneState}
        />
      </div>
      {mediaPanelResizing && <div id="dragSurface"></div>}
    </>
  );
};

export default ReaderPageLayoutView;
